import React, { useState, useEffect } from 'react';
import { apiBaseUrl } from '../config.js';

const Finanzas = ({ userToken }) => {
    // Get today's date in YYYY-MM-DD format (Argentina timezone)
    const getTodayDate = () => {
        const now = new Date();
        const argentinaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
        const year = argentinaTime.getFullYear();
        const month = String(argentinaTime.getMonth() + 1).padStart(2, '0');
        const day = String(argentinaTime.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const getTomorrowDate = () => {
        const now = new Date();
        const argentinaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
        argentinaTime.setDate(argentinaTime.getDate() + 1);
        const year = argentinaTime.getFullYear();
        const month = String(argentinaTime.getMonth() + 1).padStart(2, '0');
        const day = String(argentinaTime.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [paymentStats, setPaymentStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTomorrowDate());
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchPaymentStats();
    }, [startDate, endDate]);

    const fetchPaymentStats = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            const queryString = params.toString() ? `?${params.toString()}` : '';

            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const response = await fetch(`${apiBaseUrl}/api/metrics/payment-stats${queryString}`, { headers });

            if (response.status === 403 || response.status === 401) {
                setError('Acceso denegado. Se requieren privilegios de administrador.');
                setLoading(false);
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setPaymentStats(data.stats || null);
            }

        } catch (err) {
            setError('Error al cargar las estadísticas financieras');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => num?.toLocaleString?.() ?? num;
    const formatCurrency = (num) => `$${Number(num || 0).toLocaleString()}`;

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                padding: isMobile ? '15px' : '30px',
                backgroundColor: '#f5f7fa'
            }}>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div style={{ fontSize: '20px', color: '#666' }}>Cargando datos financieros...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                padding: isMobile ? '15px' : '30px',
                backgroundColor: '#f5f7fa'
            }}>
                <div style={{ textAlign: 'center', padding: '50px', color: '#e74c3c' }}>
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            padding: isMobile ? '15px' : '30px',
            backgroundColor: '#f5f7fa'
        }}>
            {/* Header */}
            <div style={{
                marginBottom: isMobile ? '20px' : '30px'
            }}>
                <h1 style={{
                    fontSize: isMobile ? '24px' : '32px',
                    fontWeight: '700',
                    color: '#2c3e50',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    💰 Finanzas
                </h1>
                <p style={{
                    color: '#7f8c8d',
                    fontSize: isMobile ? '14px' : '16px',
                    margin: 0
                }}>
                    Métricas financieras y análisis de pagos premium
                </p>
            </div>

            {/* Date Filter */}
            <div style={{
                backgroundColor: 'white',
                padding: isMobile ? '15px' : '20px',
                borderRadius: isMobile ? '10px' : '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e8ecef',
                marginBottom: isMobile ? '20px' : '30px'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '15px',
                    alignItems: isMobile ? 'stretch' : 'center',
                    flexWrap: 'wrap'
                }}>
                    <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600',
                        color: '#2c3e50'
                    }}>
                        Fecha Inicio:
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                marginLeft: '8px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: isMobile ? '13px' : '14px'
                            }}
                        />
                    </label>
                    <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600',
                        color: '#2c3e50'
                    }}>
                        Fecha Fin:
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                marginLeft: '8px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: isMobile ? '13px' : '14px'
                            }}
                        />
                    </label>
                    <button
                        onClick={fetchPaymentStats}
                        style={{
                            padding: '8px 20px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '600'
                        }}
                    >
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Payment Metrics Cards */}
            {paymentStats && (
                <>
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: isMobile ? '15px' : '20px',
                        marginBottom: isMobile ? '20px' : '30px'
                    }}>
                        <div style={{ 
                            padding: isMobile ? '20px' : '25px', 
                            borderRadius: isMobile ? '10px' : '12px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}>
                            <div style={{ 
                                fontSize: isMobile ? '13px' : '15px', 
                                marginBottom: '10px', 
                                opacity: 0.9,
                                fontWeight: '500'
                            }}>
                                👆 Clics en Premium
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '32px' : '38px', 
                                fontWeight: '700',
                                marginBottom: '5px'
                            }}>
                                {formatNumber(paymentStats.totalIntents)}
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                opacity: 0.8
                            }}>
                                Intentos de pago iniciados
                            </div>
                        </div>

                        <div style={{ 
                            padding: isMobile ? '20px' : '25px', 
                            borderRadius: isMobile ? '10px' : '12px', 
                            background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                            color: 'white',
                            boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)'
                        }}>
                            <div style={{ 
                                fontSize: isMobile ? '13px' : '15px', 
                                marginBottom: '10px', 
                                opacity: 0.9,
                                fontWeight: '500'
                            }}>
                                ✅ Pagos Aprobados
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '32px' : '38px', 
                                fontWeight: '700',
                                marginBottom: '5px'
                            }}>
                                {formatNumber(paymentStats.approvedPayments)}
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                opacity: 0.8
                            }}>
                                Pagos completados exitosamente
                            </div>
                        </div>

                        <div style={{ 
                            padding: isMobile ? '20px' : '25px', 
                            borderRadius: isMobile ? '10px' : '12px', 
                            background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
                            color: 'white',
                            boxShadow: '0 4px 15px rgba(243, 156, 18, 0.3)'
                        }}>
                            <div style={{ 
                                fontSize: isMobile ? '13px' : '15px', 
                                marginBottom: '10px', 
                                opacity: 0.9,
                                fontWeight: '500'
                            }}>
                                📊 Tasa de Conversión
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '32px' : '38px', 
                                fontWeight: '700',
                                marginBottom: '5px'
                            }}>
                                {paymentStats.conversionRate}%
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                opacity: 0.8
                            }}>
                                De clics a pagos completados
                            </div>
                        </div>

                        <div style={{ 
                            padding: isMobile ? '20px' : '25px', 
                            borderRadius: isMobile ? '10px' : '12px', 
                            background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                            color: 'white',
                            boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)'
                        }}>
                            <div style={{ 
                                fontSize: isMobile ? '13px' : '15px', 
                                marginBottom: '10px', 
                                opacity: 0.9,
                                fontWeight: '500'
                            }}>
                                💰 Ingresos Totales
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '32px' : '38px', 
                                fontWeight: '700',
                                marginBottom: '5px'
                            }}>
                                {formatCurrency(paymentStats.totalRevenue)}
                            </div>
                            <div style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                opacity: 0.8
                            }}>
                                ARS - Ingresos confirmados
                            </div>
                        </div>
                    </div>

                    {/* Recent Payments Table */}
                    {paymentStats.recentPayments && paymentStats.recentPayments.length > 0 && (
                        <div style={{
                            backgroundColor: 'white',
                            padding: isMobile ? '20px' : '25px',
                            borderRadius: isMobile ? '10px' : '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e8ecef'
                        }}>
                            <h2 style={{
                                fontSize: isMobile ? '18px' : '22px',
                                fontWeight: '700',
                                color: '#2c3e50',
                                marginBottom: isMobile ? '15px' : '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                📝 Pagos Recientes (Últimos 10)
                            </h2>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: isMobile ? '13px' : '14px'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ 
                                                padding: isMobile ? '10px 8px' : '12px 15px', 
                                                textAlign: 'left', 
                                                borderBottom: '2px solid #dee2e6',
                                                fontWeight: '600',
                                                color: '#2c3e50'
                                            }}>Fecha</th>
                                            <th style={{ 
                                                padding: isMobile ? '10px 8px' : '12px 15px', 
                                                textAlign: 'left', 
                                                borderBottom: '2px solid #dee2e6',
                                                fontWeight: '600',
                                                color: '#2c3e50'
                                            }}>Dirección</th>
                                            <th style={{ 
                                                padding: isMobile ? '10px 8px' : '12px 15px', 
                                                textAlign: 'right', 
                                                borderBottom: '2px solid #dee2e6',
                                                fontWeight: '600',
                                                color: '#2c3e50'
                                            }}>Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentStats.recentPayments.map((payment, index) => (
                                            <tr key={payment.id} style={{
                                                backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                            }}>
                                                <td style={{ 
                                                    padding: isMobile ? '10px 8px' : '12px 15px', 
                                                    borderBottom: '1px solid #dee2e6',
                                                    color: '#495057'
                                                }}>
                                                    {new Date(payment.createdAt).toLocaleDateString('es-AR', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td style={{ 
                                                    padding: isMobile ? '10px 8px' : '12px 15px', 
                                                    borderBottom: '1px solid #dee2e6',
                                                    color: '#495057'
                                                }}>
                                                    {payment.property?.direccion || 'N/A'}
                                                </td>
                                                <td style={{ 
                                                    padding: isMobile ? '10px 8px' : '12px 15px', 
                                                    borderBottom: '1px solid #dee2e6',
                                                    textAlign: 'right',
                                                    fontWeight: '600',
                                                    color: '#27ae60'
                                                }}>
                                                    {formatCurrency(payment.price)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Finanzas;
